import { useEffect, useId, useState, type FormEvent } from "react";
import { z } from "zod";
import type { AgentGender, AgentSettings, EmojiLevel, Formality, MessageLength, Persona } from "../../shared/contracts";
import { AGENT_GENDERS, DEFAULT_SETTINGS, EMOJI_LEVELS, FORMALITIES, MESSAGE_LENGTHS, PERSONA_VALUES } from "../../shared/contracts";
import { AvatarStudio } from "../components/avatar-studio";
import { CharacterCreator } from "../components/character-creator";
import { Button, ErrorNotice, Field, Loading, Notice, Panel, RadioCard, Segmented, Textarea } from "../components/ui";
import { useSettings } from "../lib/agent";
import { api, errorText } from "../lib/api";
import { EMOJI_LEVEL_OPTIONS, FORMALITY_OPTIONS, GENDER_OPTIONS, MESSAGE_LENGTH_OPTIONS, PERSONAS } from "../lib/format";
import { usePageTitle } from "../lib/hooks";
import { invalidate, mutateResource } from "../lib/store";

/** The identity fields this page edits (the avatar and the color are saved by their own controls, at once). */
interface FormState {
  agentName: string;
  agentEmoji: string;
  persona: Persona;
  personaNotes: string;
  emojiLevel: EmojiLevel;
  messageLength: MessageLength;
  formality: Formality;
  agentGender: AgentGender;
}

type Errors = Partial<Record<keyof FormState, string>>;

function toForm(raw: AgentSettings): FormState {
  const s: AgentSettings = { ...DEFAULT_SETTINGS, ...raw };
  return {
    agentName: s.agentName,
    agentEmoji: s.agentEmoji,
    persona: s.persona,
    personaNotes: s.personaNotes,
    emojiLevel: s.emojiLevel,
    messageLength: s.messageLength,
    formality: s.formality,
    agentGender: s.agentGender,
  };
}

const schema = z.object({
  agentName: z.string().trim().min(1, "Donne un nom à ton agent.").max(40, "40 caractères maximum."),
  agentEmoji: z
    .string()
    .trim()
    .regex(/^[^\s\p{Cc}]{1,16}$/u, "Choisis un emoji."),
  persona: z.enum(PERSONA_VALUES),
  personaNotes: z.string().trim().max(1000, "1 000 caractères maximum."),
  emojiLevel: z.enum(EMOJI_LEVELS),
  messageLength: z.enum(MESSAGE_LENGTHS),
  formality: z.enum(FORMALITIES),
  agentGender: z.enum(AGENT_GENDERS),
});

/** Only the fields that changed (PATCH semantics). */
function diff(before: AgentSettings, after: FormState): Partial<FormState> {
  const out: Partial<Record<keyof FormState, unknown>> = {};
  // The name is saved with the face (CharacterCreator).
  for (const key of (Object.keys(after) as (keyof FormState)[]).filter((k) => k !== "agentName")) {
    if (JSON.stringify(after[key]) !== JSON.stringify(before[key])) out[key] = after[key];
  }
  return out as Partial<FormState>;
}

/** /agent — « Son visage » (V2Visage): its character and name, then its personality and way of writing, or an image. */
export function AgentPage() {
  usePageTitle("Son visage");
  const settings = useSettings();

  if (settings.loading) return <Loading />;
  if (!settings.data) return <ErrorNotice message={settings.error?.message ?? "Impossible de charger ton agent."} onRetry={() => void settings.reload()} />;
  const s = settings.data;

  return (
    <div className="flex flex-col gap-8">
      <CharacterCreator settings={s} />
      <AgentForm settings={s} />
      <Panel id="dessin" title="Ou une image">
        <AvatarStudio settings={s} />
      </Panel>
    </div>
  );
}

function AgentForm({ settings }: { settings: AgentSettings }) {
  const [form, setForm] = useState<FormState>(() => toForm(settings));
  const [dirty, setDirty] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const p = useId();
  const id = (name: string) => `${p}-${name}`;

  // Updates pushed from elsewhere (another tab, the agent itself) refresh the form until the user starts editing.
  useEffect(() => {
    if (!dirty) setForm(toForm(settings));
  }, [settings, dirty]);

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
    setDirty(true);
    setSaved(false);
    setErrors((e) => (e[key] ? { ...e, [key]: undefined } : e));
  }

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (busy) return;
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      const next: Errors = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] as keyof FormState | undefined;
        if (key && !next[key]) next[key] = issue.message;
      }
      setErrors(next);
      setError("Vérifie les champs en rouge.");
      return;
    }
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const patch = diff(settings, parsed.data);
      const next = Object.keys(patch).length ? await api("PATCH /api/settings", { body: patch }) : settings;
      mutateResource<AgentSettings>("settings", () => next);
      invalidate("dashboard");
      setForm(toForm(next));
      setDirty(false);
      setErrors({});
      setSaved(true);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} noValidate className="space-y-6">
      <Panel id="personnalite" title="Sa personnalité">
        <fieldset>
          <legend className="sr-only">Personnalité</legend>
          <div className="grid gap-2 sm:grid-cols-2">
            {PERSONAS.map((o) => (
              <RadioCard key={o.value} name="persona" value={o.value} label={o.label} checked={form.persona === o.value} onChange={() => set("persona", o.value)}>
                {o.description}
              </RadioCard>
            ))}
          </div>
        </fieldset>
        <Field
          label="Décris sa personnalité (facultatif)"
          htmlFor={id("personaNotes")}
          error={errors.personaNotes}
          hint="Ex. un brin taquin, passionné de cuisine. Ça change son ton, jamais ses règles."
        >
          <Textarea id={id("personaNotes")} value={form.personaNotes} maxLength={1000} className="min-h-24" onChange={(e) => set("personaNotes", e.target.value)} />
        </Field>
      </Panel>

      <Panel id="ecriture" title="Sa façon d'écrire">
        <Segmented name="emojiLevel" legend="Emojis" value={form.emojiLevel} options={EMOJI_LEVEL_OPTIONS} onChange={(v) => set("emojiLevel", v)} />
        <Segmented
          name="messageLength"
          legend="Longueur des messages"
          value={form.messageLength}
          options={MESSAGE_LENGTH_OPTIONS}
          onChange={(v) => set("messageLength", v)}
        />
        <Segmented name="formality" legend="Tutoiement ou vouvoiement" value={form.formality} options={FORMALITY_OPTIONS} onChange={(v) => set("formality", v)} />
        <Segmented
          name="agentGender"
          legend="Ses accords"
          value={form.agentGender}
          options={GENDER_OPTIONS.map((o) => ({ value: o.value, label: o.label, title: o.example }))}
          onChange={(v) => set("agentGender", v)}
          hint={GENDER_OPTIONS.find((o) => o.value === form.agentGender)?.example}
        />
      </Panel>

      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" busy={busy}>
          Enregistrer
        </Button>
        {saved ? (
          <span className="text-[13px] font-medium text-success" role="status">
            Modifications enregistrées
          </span>
        ) : dirty ? (
          <span className="text-[12.5px] text-muted">Modifications non enregistrées</span>
        ) : null}
        {error ? (
          <Notice tone="danger" role="alert" className="w-full">
            {error}
          </Notice>
        ) : null}
      </div>
    </form>
  );
}
